import { FileText, ShoppingCart, Scale, AlertTriangle, Ban, RefreshCw } from "lucide-react";
import { Container } from "@/components/ui/container";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useSeo } from "@/hooks/useSeo";

interface AGBSeiteProps {
  onNavigate: (path: string) => void;
}

export function AGBSeite({ onNavigate }: AGBSeiteProps) {
  useSeo({
    title: "Allgemeine Geschäftsbedingungen - zelt.kaufen",
    description: "Allgemeine Geschäftsbedingungen (AGB) von zelt.kaufen - Nutzungsbedingungen für das Vergleichsportal.",
    type: "website",
  });

  return (
    <div className="min-h-screen py-12">
      <Container className="max-w-4xl">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Allgemeine Geschäftsbedingungen</h1>
          <p className="text-lg text-muted-foreground">
            Nutzungsbedingungen für zelt.kaufen
          </p>
        </div>

        <div className="space-y-8">
          {/* Geltungsbereich */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-primary" />
                § 1 Geltungsbereich
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-muted-foreground leading-relaxed">
              <p>
                Diese Allgemeinen Geschäftsbedingungen (AGB) gelten für die Nutzung der Website
                zelt.kaufen, betrieben von der Pykez UG (haftungsbeschränkt), Danziger Str. 4a,
                36251 Bad Hersfeld.
              </p>
              <p>
                zelt.kaufen ist ein Vergleichsportal für Zelte und Campingausrüstung. Wir verkaufen
                selbst keine Produkte, sondern vermitteln Informationen und leiten bei Interesse an
                die jeweiligen Online-Shops weiter.
              </p>
            </CardContent>
          </Card>

          {/* Leistungsbeschreibung */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ShoppingCart className="h-5 w-5 text-primary" />
                § 2 Leistungsbeschreibung
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-muted-foreground leading-relaxed">
              <p>
                zelt.kaufen bietet einen kostenlosen Vergleichsservice für Zelte und
                Campingausrüstung. Die auf der Website dargestellten Produktinformationen,
                Bewertungen und Preise dienen ausschließlich der Information und Orientierung.
              </p>
              <p>
                Wir bemühen uns, alle Angaben aktuell und korrekt zu halten, können jedoch keine
                Gewähr für die Vollständigkeit, Richtigkeit und Aktualität der bereitgestellten
                Informationen übernehmen. Verbindlich sind allein die Angaben der jeweiligen
                Händler bzw. Hersteller.
              </p>
            </CardContent>
          </Card>

          {/* Affiliate */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <RefreshCw className="h-5 w-5 text-primary" />
                § 3 Affiliate-Links und Weiterleitung
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-muted-foreground leading-relaxed">
              <p>
                Unsere Website enthält Links zu externen Online-Shops (Affiliate-Links). Beim Klick
                auf einen solchen Link werden Sie auf die Website des jeweiligen Anbieters
                weitergeleitet. Bei einem Kauf über diese Links erhalten wir eine Provision vom
                jeweiligen Shop-Betreiber.
              </p>
              <p>
                Es entstehen Ihnen durch die Nutzung der Affiliate-Links keine zusätzlichen Kosten.
                Der Kaufvertrag kommt ausschließlich zwischen Ihnen und dem jeweiligen Händler
                zustande. Wir sind nicht Vertragspartner dieser Geschäfte.
              </p>
            </CardContent>
          </Card>

          {/* Haftung */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-primary" />
                § 4 Haftungsbeschränkung
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-muted-foreground leading-relaxed">
              <p>
                Die Inhalte dieser Website werden mit größtmöglicher Sorgfalt erstellt. Der Anbieter
                übernimmt jedoch keine Gewähr für die Richtigkeit, Vollständigkeit und Aktualität
                der bereitgestellten Inhalte.
              </p>
              <p>
                Die Nutzung der Website erfolgt auf eigene Gefahr. Schadensersatzansprüche gegen
                den Anbieter, welche sich auf Schäden materieller oder ideeller Art beziehen, die
                durch die Nutzung oder Nichtnutzung der dargebotenen Informationen bzw. durch die
                Nutzung fehlerhafter und unvollständiger Informationen verursacht wurden, sind
                grundsätzlich ausgeschlossen.
              </p>
            </CardContent>
          </Card>

          {/* Urheberrecht */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Ban className="h-5 w-5 text-primary" />
                § 5 Urheberrecht
              </CardTitle>
            </CardHeader>
            <CardContent className="text-muted-foreground leading-relaxed">
              <p>
                Die durch den Seitenbetreiber erstellten Inhalte und Werke auf diesen Seiten
                unterliegen dem deutschen Urheberrecht. Beiträge Dritter sind als solche
                gekennzeichnet. Die Vervielfältigung, Bearbeitung, Verbreitung und jede Art der
                Verwertung außerhalb der Grenzen des Urheberrechtes bedürfen der schriftlichen
                Zustimmung des jeweiligen Autors bzw. Erstellers.
              </p>
            </CardContent>
          </Card>

          {/* Schlussbestimmungen */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Scale className="h-5 w-5 text-primary" />
                § 6 Schlussbestimmungen
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-muted-foreground leading-relaxed">
              <p>
                Es gilt das Recht der Bundesrepublik Deutschland. Gerichtsstand ist Bad Hersfeld,
                soweit gesetzlich zulässig.
              </p>
              <p>
                Sollten einzelne Bestimmungen dieser AGB unwirksam sein oder werden, bleibt die
                Wirksamkeit der übrigen Bestimmungen unberührt.
              </p>
              <p className="text-sm">
                Stand: Februar 2026
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Back Link */}
        <div className="mt-12 text-center">
          <button
            onClick={() => onNavigate("/")}
            className="text-primary hover:underline"
          >
            ← Zurück zur Startseite
          </button>
        </div>
      </Container>
    </div>
  );
}
